import {
  ActionButton,
  Breadcrumbs,
  IFlow,
  LOCAL_STORAGE_THINGS_VIEW_MODE_KEY,
  makeDynamicEntitiesPageStore,
  PageViewModeEnum,
  PageViewModeToolbar,
  useCreateAssetActions,
  usePerformanceGraphQLParameters,
  useSaveAndSetViewMode,
} from '@diana/core';
import { AddRounded as AddIcon } from '@mui/icons-material';
import { useIsFetching } from '@tanstack/react-query';
import { Fragment, memo } from 'react';
import { useBreadcrumbsRoot } from '../../hooks/useBreadcrumbsRoot';
import { BuilderButton } from './BuilderButton';
import { getThingsGraphQLWorksQueryKey } from './Support';

export const HeaderLeft = memo<{ flow: IFlow; peoplePage: boolean }>(
  (props) => {
    const { flow, peoplePage } = props;
    const breadcrumbRoot = useBreadcrumbsRoot(peoplePage ? 'people' : 'assets');
    const breadCrumbs = peoplePage
      ? [breadcrumbRoot]
      : [breadcrumbRoot, { title: flow.displayName, hidden: false }];

    return <Breadcrumbs items={breadCrumbs} />;
  },
  (prev, next) => prev.flow.id === next.flow.id,
);

export const HeaderRight = memo<{
  flow: IFlow;
  handleCsvButtonClick: () => void;
  openInSidePeek?: boolean;
  isLoading?: boolean;
  peoplePage: boolean;
}>(
  (props) => {
    const { flow, handleCsvButtonClick, isLoading, openInSidePeek = true, peoplePage } = props;
    const useEntitiesPageStore = makeDynamicEntitiesPageStore(flow.flowOriginId);

    const availableFilters = useEntitiesPageStore((store) => store.availableFilters);
    const filtersValues = useEntitiesPageStore((store) => store.filtersValues);
    const viewMode = useEntitiesPageStore((store) => store.viewMode);
    const setViewMode = useEntitiesPageStore((store) => store.setViewMode);
    const supportedViewModes = useEntitiesPageStore((store) => store.supportedViewModes);

    const createAsset = useCreateAssetActions({
      flowOriginId: flow.flowOriginId,
      openInSidePeek,
      peoplePage,
    });

    const queryParameters = usePerformanceGraphQLParameters({
      flows: flow ? [flow] : [],
      filters: availableFilters,
      filtersValues,
    });

    const isFetchingEntitiesWorks = useIsFetching({
      queryKey: getThingsGraphQLWorksQueryKey(flow.id, queryParameters.filterProperties),
    });

    const { saveAndSetViewMode } = useSaveAndSetViewMode({
      flowId: flow.flowOriginId,
      storageKey: LOCAL_STORAGE_THINGS_VIEW_MODE_KEY,
      setViewMode,
    });

    const onViewChange = (viewMode: PageViewModeEnum) => {
      saveAndSetViewMode(viewMode);
    };

    return (
      <Fragment>
        {!!createAsset.actions.length && !isLoading && (
          <ActionButton
            actions={createAsset.actions}
            startIcon={<AddIcon color="inherit" />}
            preventUseMonoActionIcon
            sx={({ spacing }) => ({ height: spacing(4) })}
          >
            {createAsset.displayName}
          </ActionButton>
        )}
        {!!flow && <BuilderButton flow={flow} />}
        <PageViewModeToolbar
          viewMode={viewMode}
          onViewChange={onViewChange}
          supportedViewModes={supportedViewModes}
          isCsvButtonDisabled={isFetchingEntitiesWorks > 0}
          onCsvClick={
            [PageViewModeEnum.Table, PageViewModeEnum.TableExpanded].includes(viewMode)
              ? handleCsvButtonClick
              : undefined
          }
        />
        {createAsset.content}
      </Fragment>
    );
  },
  (prev, next) => prev.flow.id === next.flow.id,
);
